import { executeJSmodules } from '@archetype-themes/utils/utils'
import { EVENTS } from '@archetype-themes/utils/events'
import { applyCartCuratorNotes } from 'components/mm-curator-note-edit'

/*============================================================================
  MM QuickShop override
  Replaces components/quick-shop via mm-import-map-overrides.liquid so core
  assets/quick-shop.js can stay untouched on theme updates.

  Listeners are registered on document (not the custom element) because quick-shop
  islands hydrate lazily via is-land — clicks can fire before the element
  constructor runs, leaving stale content in the shared tooltip container.

  templates/product.modal.json custom blocks mirror product.json — keep in sync
  if final-sale / third-party / bulk-ship business rules change.
==============================================================================*/

const QUICK_SHOP_CONTAINER = '[data-tool-tip-content]'
const CACHE_VIEW_SUFFIX = ':modal'
const MAX_CACHE_SIZE = 20
const cache = new Map()
const inflight = new Map()
let quickShopOpenGeneration = 0

const LOADING_HTML =
  '<div class="quick-shop__loading" aria-busy="true" aria-live="polite"><span class="quick-shop__loading-spinner"></span></div>'

function cacheGet(key) {
  if (!cache.has(key)) return undefined
  const value = cache.get(key)
  cache.delete(key)
  cache.set(key, value)
  return value
}

function cacheSet(key, value) {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > MAX_CACHE_SIZE) {
    cache.delete(cache.keys().next().value)
  }
}

function getProductFromTrigger(trigger) {
  const gridItem = trigger.closest('.grid-product')
  const productEl = gridItem?.firstElementChild

  return {
    handle: productEl?.getAttribute('data-product-handle'),
    prodId: productEl?.getAttribute('data-product-id')
  }
}

function getCacheKey(prodId) {
  return `${prodId}${CACHE_VIEW_SUFFIX}`
}

function processQuickShopHTML(productElement) {
  if (!productElement) return

  productElement.querySelector('.breadcrumb')?.remove()

  const variantPicker = productElement.querySelector('block-variant-picker')
  if (variantPicker) variantPicker.removeAttribute('data-update-url')
}

function isValidQuickShopData(data) {
  return Boolean(data?.childNodes?.length)
}

async function fetchQuickShopData({ handle, prodId }) {
  const productUrl = `${window.Shopify.routes.root}/products/${handle}`.replace('//', '/')
  const fetchUrl = `${productUrl}?view=modal`

  const response = await fetch(fetchUrl)
  if (!response.ok) {
    console.error(`Quick shop fetch failed (${response.status}): ${fetchUrl}`)
    return null
  }

  const text = await response.text()
  const responseHTML = new DOMParser().parseFromString(text, 'text/html')
  const div = responseHTML.querySelector(`.page-content[data-product-id="${prodId}"]`)

  if (!div) {
    console.error(`Quick shop content not found for product ${prodId}`)
    return null
  }

  if (typeof addQuickShopDetails === 'function') {
    addQuickShopDetails(div, productUrl)
  } else {
    processQuickShopHTML(div)
  }

  div.dataset.modal = 'true'
  const fragment = document.createDocumentFragment()
  fragment.appendChild(div.cloneNode(true))

  window.dispatchEvent(new CustomEvent(`quickshop:loaded-${prodId}`))
  document.dispatchEvent(
    new CustomEvent('quickshop:loaded', {
      detail: { productId: prodId, handle }
    })
  )

  return fragment
}

async function ensureQuickShopData(product) {
  const { handle, prodId } = product
  if (!handle || !prodId) return null

  const cacheKey = getCacheKey(prodId)

  const cached = cacheGet(cacheKey)
  if (cached) {
    return cached
  }

  if (inflight.has(cacheKey)) {
    return inflight.get(cacheKey)
  }

  const loadPromise = fetchQuickShopData({ handle, prodId })
    .then((data) => {
      if (isValidQuickShopData(data)) {
        cacheSet(cacheKey, data)
        return data
      }
      return null
    })
    .catch((error) => {
      console.error('Error:', error)
      return null
    })
    .finally(() => {
      inflight.delete(cacheKey)
    })

  inflight.set(cacheKey, loadPromise)
  return loadPromise
}

function showLoadingState(container) {
  container.innerHTML = LOADING_HTML
}

document.addEventListener(EVENTS.ajaxProductAdded, () => cache.clear())

function mountQuickShopContent(container, data) {
  container.innerHTML = ''
  container.appendChild(data.cloneNode(true))
  applyCartCuratorNotes(container, true)
}

function openQuickShopModal(detail) {
  document.dispatchEvent(
    new CustomEvent('tooltip:open', {
      detail: { ...detail, _mmQuickShopHandled: true },
      bubbles: true
    })
  )
}

function initQuickShopModules(prodId) {
  if (Shopify && Shopify.PaymentButton) {
    Shopify.PaymentButton.init()
  }

  const scripts = document.querySelectorAll(`tool-tip [data-product-id="${prodId}"] script[type="module"]`)
  executeJSmodules(scripts)
}

function finishQuickShopOpen({ container, data, product, quickShopEl, generation }) {
  // Generation only guards against a newer open superseding this one — not against
  // the user closing the modal with nothing reopened. Listeners on quickshop:opened
  // should not assume the modal is still visible when this runs.
  if (generation !== quickShopOpenGeneration || !container) return

  if (isValidQuickShopData(data)) {
    mountQuickShopContent(container, data)
    quickShopEl?.dispatchEvent(
      new CustomEvent('quickshop:opened', {
        bubbles: true
      })
    )
    initQuickShopModules(product.prodId)
  } else {
    container.innerHTML =
      '<p class="quick-shop__loading-error">Unable to load product. Please try again.</p>'
  }
}

// Preload on hover/focus (debounced by tool-tip-trigger)
document.addEventListener('tooltip:interact', (e) => {
  if (e.detail?.context !== 'QuickShop') return
  if (matchMedia('(max-width: 768px)').matches) return

  ensureQuickShopData(getProductFromTrigger(e.target))
})

// Open modal immediately; load content in background (cached = instant content)
document.addEventListener(
  'tooltip:open',
  (e) => {
    if (e.detail?.context !== 'QuickShop') return
    if (e.detail._mmQuickShopHandled) return
    if (matchMedia('(max-width: 768px)').matches) return

    e.stopImmediatePropagation()

    const generation = ++quickShopOpenGeneration
    const container = document.querySelector(QUICK_SHOP_CONTAINER)
    const product = getProductFromTrigger(e.target)
    const quickShopEl = e.target.closest('quick-shop')

    if (!product.handle || !product.prodId || !container) return

    const cached = cacheGet(getCacheKey(product.prodId))

    if (isValidQuickShopData(cached)) {
      mountQuickShopContent(container, cached)
      openQuickShopModal(e.detail)
      quickShopEl?.dispatchEvent(
        new CustomEvent('quickshop:opened', {
          bubbles: true
        })
      )
      initQuickShopModules(product.prodId)
      return
    }

    showLoadingState(container)
    openQuickShopModal(e.detail)

    ensureQuickShopData(product).then((data) => {
      finishQuickShopOpen({ container, data, product, quickShopEl, generation })
    })
  },
  true
)

class QuickShop extends HTMLElement {}

if (!customElements.get('quick-shop')) {
  customElements.define('quick-shop', QuickShop)
}
